import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { X, Coins, Gift, ChevronLeft, ChevronRight } from "lucide-react";
import { ContestWinnerBadgeGroup, ContestWinnerItem } from "types/giveaway";
import { toDate } from "pages/Admin/giveawayManagement/giveawayFormUtils";
import winnerHeaderImg from "assets/images/giveaway/winnerheader.png";
import crownImg from "assets/images/giveaway/firstCrown.png";
import firstPrizeImg from "assets/images/giveaway/firstPrize.png";
import secondPrizeImg from "assets/images/giveaway/secondPrize.png";
import thirdPrizeImg from "assets/images/giveaway/thirdPrize.png";

const podiumStyle: Record<1 | 2 | 3, { avatarSize: string; blockImg: string }> = {
  1: { avatarSize: "w-16 h-16", blockImg: firstPrizeImg },
  2: { avatarSize: "w-12 h-12", blockImg: secondPrizeImg },
  3: { avatarSize: "w-12 h-12", blockImg: thirdPrizeImg },
};

interface GiveawayWinnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** One entry per contest with at least one currently-visible winner. */
  groups: ContestWinnerBadgeGroup[];
}

const formatDateTime = (value?: string | null) => {
  const date = toDate(value);
  return date ? format(date, "dd MMM yyyy") : "-";
};

const formatWinnerDisplayName = (winner?: { displayName?: string; winnerName?: string }): string => {
  if (!winner) return "Winner";
  const raw = winner.displayName || winner.winnerName || "";
  const cleaned = raw.replace(/\b(undefined|null)\b/gi, "").replace(/\s+/g, " ").trim();
  return cleaned || "Winner";
};

const getInitials = (name?: string): string => {
  if (!name || typeof name !== "string") return "W";
  const cleaned = name.replace(/\b(undefined|null)\b/gi, "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "W";
  const parts = cleaned.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
};

const WinnerAvatar: React.FC<{ winner: ContestWinnerItem; size?: string }> = ({
  winner,
  size = "w-10 h-10",
}) => {
  const displayName = formatWinnerDisplayName(winner);
  return winner.profilePictureUrl ? (
    <img
      src={winner.profilePictureUrl}
      alt={displayName}
      className={`${size} rounded-full object-cover border-2 border-amber-400 shadow-sm shrink-0`}
    />
  ) : (
    <div
      className={`${size} rounded-full bg-gradient-to-tr from-amber-400 to-amber-200 text-[#3B0764] text-xs font-black flex items-center justify-center shadow-sm border-2 border-amber-400 shrink-0`}
    >
      {getInitials(displayName)}
    </div>
  );
};

const GiveawayWinnerModal: React.FC<GiveawayWinnerModalProps> = ({
  isOpen,
  onClose,
  groups,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Reset to the first contest each time the modal is (re)opened.
  useEffect(() => {
    if (isOpen) setActiveIndex(0);
  }, [isOpen]);

  if (!isOpen || groups.length === 0) return null;

  const group = groups[activeIndex % groups.length];
  const hasMultiple = groups.length > 1;

  const goPrev = () =>
    setActiveIndex((i) => (i - 1 + groups.length) % groups.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % groups.length);

  const tierWinners = ([1, 2, 3] as const)
    .map((position) => ({
      position,
      winner: group.winners.find(
        (w) => !w.isAdditionalWinner && w.position === position,
      ),
    }))
    .filter((entry) => !!entry.winner);

  const additionalWinners = group.winners.filter((w) => w.isAdditionalWinner);
  const latestAnnouncedOn = group.winners.find((w) => w.announcedDate)?.announcedDate;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleUp border border-gray-100 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Banner */}
        <div className="relative shrink-0 bg-[#0F071D] flex flex-col items-center">
          <img
            src={winnerHeaderImg}
            alt="Giveaway Winner — Congratulations!"
            className="w-full h-auto block object-cover"
          />

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition backdrop-blur-md z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Contest name — elegant badge styled to match header theme */}
          <div className="w-full bg-[#120824] py-2.5 flex items-center justify-center border-b border-violet-900/20">
            <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-[#1E0D3B] border border-amber-400/30 text-amber-300 text-xs font-bold tracking-wide shadow-sm">
              {group.contestName}
            </span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Prev/Next + dot indicators when there are multiple contests */}
          {hasMultiple && (
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous contest"
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-1.5">
                {groups.map((g, idx) => (
                  <button
                    key={g.contestId}
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    aria-label={`Show contest ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === activeIndex
                        ? "w-5 bg-amber-500"
                        : "w-1.5 bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={goNext}
                aria-label="Next contest"
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Prize tier winners — only tiers that were actually published */}
          {tierWinners.length > 0 && (
            <div className="-mx-6 flex items-end justify-center gap-0 px-5 pt-8 pb-2 bg-gray-50">
              {([2, 1, 3] as const).map((slot) => {
                const entry = tierWinners.find((t) => t.position === slot);
                if (!entry) return null;
                const winner = entry.winner!;
                const style = podiumStyle[slot];
                return (
                  <div key={slot} className="flex flex-col items-center w-20 min-w-0">
                    <div className="relative mb-1.5">
                      {slot === 1 && (
                        <img
                          src={crownImg}
                          alt=""
                          className="w-9 h-9 absolute top-[-0.80rem] left-[90%] -translate-x-1/2 drop-shadow"
                          aria-hidden="true"
                        />
                      )}
                      <WinnerAvatar winner={winner} size={style.avatarSize} />
                    </div>
                    <p className="text-xs font-semibold text-gray-900 text-center break-words w-full leading-tight">
                      {formatWinnerDisplayName(winner)}
                    </p>
                    {winner.prizeWon && (
                      <span className="mt-1 inline-block max-w-full truncate rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-800 shadow-sm">
                        {winner.prizeWon}
                      </span>
                    )}
                    <img src={style.blockImg} alt={`${slot}`} className="mt-2 w-full h-auto" />
                  </div>
                );
              })}
            </div>
          )}

          {/* Additional Winners — scrollable if many */}
          {additionalWinners.length > 0 && (
            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200/80 space-y-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <Gift className="w-4 h-4 text-violet-600" />
                <span>Additional Winners</span>
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {additionalWinners.map((winner) => (
                  <div
                    key={winner.winnerId}
                    className="flex items-center gap-3 p-2.5 bg-white rounded-xl border border-gray-200 shadow-sm"
                  >
                    <WinnerAvatar winner={winner} size="w-8 h-8" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-gray-800 truncate">
                        {formatWinnerDisplayName(winner)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] font-bold text-gray-800 shrink-0">
                      <Coins className="w-3.5 h-3.5 text-amber-600" />
                      <span>
                        {winner.tokensCredited != null && Number(winner.tokensCredited) > 0
                          ? `+${winner.tokensCredited} Tokens`
                          : "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {latestAnnouncedOn && (
            <p className="text-[11px] text-gray-400 text-center">
              Announced on {formatDateTime(latestAnnouncedOn)}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-center shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto min-w-[160px] px-6 py-2.5 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#3B0764] font-bold text-sm rounded-xl shadow-md hover:opacity-95 hover:shadow-lg transition-all"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiveawayWinnerModal;
